import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Spin,
  Empty,
  Popconfirm,
  message,
  Dropdown,
  Menu,
  Modal,
  Result,
  Breadcrumb,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllApplicationsDetails, deleteApplication } from "../actions/applicationactions";
import { useNavigate, Link } from "react-router-dom";
import Application from "./Application";
import UpdateComponentModal from "./UpdateComponentModal";
import "./ApplicationTable.css";

const ApplicationTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { applications, status, error } = useSelector((state) => state.applications);

  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [isUpdateComponentModalVisible, setIsUpdateComponentModalVisible] =
    useState(false);
  const [selectedComponents, setSelectedComponents] = useState([]);

  useEffect(() => {
    dispatch(fetchAllApplicationsDetails());
  }, [dispatch]);

  const handleDelete = (id) => {
    dispatch(deleteApplication(id))
      .unwrap()
      .then(() => {
        message.success("Application deleted successfully");
        dispatch(fetchAllApplicationsDetails());
      })
      .catch((error) => {
        message.error("Failed to delete application: " + error);
      });
  };

  const handleUpdateInfo = (record) => {
    setSelectedRecord(record);
    setIsUpdateModalVisible(true);
  };

  const handleUpdateComponent = (record) => {
    if (record.components && record.components.length > 0) {
      setSelectedComponents(record.components);
      setIsUpdateComponentModalVisible(true);
    } else {
      message.warning("No component available to update.");
    }
  };

  const handleComponentsUpdated = () => {
    dispatch(fetchAllApplicationsDetails());
  };

  const columns = [
    {
      title: "Application Name",
      dataIndex: "applicationName",
      key: "applicationName",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Number of Components",
      key: "componentsCount",
      render: (_, record) => (record.components ? record.components.length : 0),
    },
    {
      title: "Total Jobs",
      key: "jobsCount",
      render: (_, record) =>
        record.components
          ? record.components.reduce(
              (total, comp) => total + (comp.jobs ? comp.jobs.length : 0),
              0
            )
          : 0,
    },
    {
      title: "Total Channels",
      key: "channelsCount",
      render: (_, record) => (record.channels ? record.channels.length : 0),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const updateMenu = (
          <Menu>
            <Menu.Item key="info" onClick={() => handleUpdateInfo(record)}>
              Update Application Information
            </Menu.Item>
            <Menu.Item key="components" onClick={() => handleUpdateComponent(record)}>
              Update Components
            </Menu.Item>
            <Menu.Item
              key="channels"
              onClick={() =>
                navigate(`/applications/update/channels/${record.applicationId}`)
              }
            >
              Update Channels
            </Menu.Item>
          </Menu>
        );

        return (
          <div className="table-buttons">
            <Button
              type="primary"
              className="details-btn"
              onClick={() => navigate(`/applications/${record.applicationId}`)}
            >
              Details
            </Button>
            <Dropdown overlay={updateMenu}>
              <Button type="default" className="update-btn">
                Update
              </Button>
            </Dropdown>
            <Popconfirm
              title="Are you sure to delete this application?"
              onConfirm={() => handleDelete(record.applicationId)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger className="delete-btn">
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const appsArray = Array.isArray(applications) ? applications : [];
  const isEmpty = status === "succeeded" && appsArray.length === 0;

  let content;

  if (status === "loading" || status === "idle") {
    content = (
      <div className="app-table-center">
        <Spin size="large" />
        <p className="app-table-loading-text">Loading applications…</p>
      </div>
    );
  } else if (status === "failed" || error) {
    content = (
      <div className="app-table-center">
        <Result
          status="error"
          title="Could not load applications"
          subTitle={
            <>
              We were unable to retrieve the list of applications.
              <br />
              This may mean the backend is temporarily unavailable.
            </>
          }
          extra={
            <Button type="primary" onClick={() => dispatch(fetchAllApplicationsDetails())}>
              Retry
            </Button>
          }
        >
          {error && (
            <div className="error-details">
              <span style={{ fontSize: 12, opacity: 0.7 }}>
                Technical details: {String(error)}
              </span>
            </div>
          )}
        </Result>
      </div>
    );
  } else if (isEmpty) {
    content = (
      <div className="app-table-center">
        <Empty description="No Applications Available" />
      </div>
    );
  } else {
    content = (
      <Table
        columns={columns}
        dataSource={appsArray}
        rowKey="applicationId"
        pagination={{ pageSize: 5 }}
      />
    );
  }

  return (
    <>
      {/* Breadcrumb + header */}
      <div className="page-header">
        <div className="page-breadcrumb-wrapper">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">Home</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Application Overview</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div className="application-table-container">
        <h2 className="table-title">📊 Application Overview</h2>

        {content}

        {/* Update Application Information Modal */}
        <Modal
          title="Update Application Information"
          open={isUpdateModalVisible}
          footer={null}
          onCancel={() => {
            setIsUpdateModalVisible(false);
            setSelectedRecord(null);
          }}
        >
          {selectedRecord && (
            <Application
              isUpdate={true}
              initialData={{
                applicationId: selectedRecord.applicationId,
                applicationName: selectedRecord.applicationName,
                description: selectedRecord.applicationDescription,
                trial: selectedRecord.trial,
                responsible: selectedRecord.responsible,
                contact: selectedRecord.contact,
                type: selectedRecord.type,
                date: selectedRecord.date,
              }}
              onClose={() => {
                setIsUpdateModalVisible(false);
                setSelectedRecord(null);
              }}
            />
          )}
        </Modal>

        {/* Update Components Modal */}
        {isUpdateComponentModalVisible &&
          selectedComponents &&
          selectedComponents.length > 0 && (
            <UpdateComponentModal
              visible={isUpdateComponentModalVisible}
              onClose={() => {
                setIsUpdateComponentModalVisible(false);
                setSelectedComponents([]);
              }}
              components={selectedComponents}
              onUpdated={handleComponentsUpdated}
            />
          )}
      </div>
    </>
  );
};

export default ApplicationTable;
